import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia,
  Container,
  Paper,
  Stack
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ChatIcon from '@mui/icons-material/Chat';
import StarIcon from '@mui/icons-material/Star';
import PaymentIcon from '@mui/icons-material/Payment';

const HomePage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <LocalShippingIcon fontSize="large" color="primary" />,
      title: 'Transporte de cargas',
      description: 'Conectamos personas que necesitan transportar cargas con transportistas disponibles en toda España.'
    },
    {
      icon: <ChatIcon fontSize="large" color="primary" />,
      title: 'Chat en tiempo real',
      description: 'Comunícate directamente con transportistas o clientes para coordinar todos los detalles del servicio.'
    },
    {
      icon: <StarIcon fontSize="large" color="primary" />,
      title: 'Sistema de valoraciones',
      description: 'Califica y revisa las valoraciones de otros usuarios para garantizar servicios de calidad.'
    },
    {
      icon: <PaymentIcon fontSize="large" color="primary" />,
      title: 'Pagos seguros',
      description: 'Utiliza nuestro sistema de PackCoins para realizar pagos seguros dentro de la plataforma.'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Paper 
        sx={{
          position: 'relative',
          backgroundColor: 'grey.800',
          color: '#fff',
          mb: 4,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundImage: 'url(https://source.unsplash.com/random?truck)',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,
            backgroundColor: 'rgba(0,0,0,.5)',
          }}
        />
        <Grid container>
          <Grid item md={6}>
            <Box
              sx={{
                position: 'relative',
                p: { xs: 3, md: 6 },
                pr: { md: 0 },
                minHeight: 400,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography component="h1" variant="h3" color="inherit" gutterBottom>
                ToPack
              </Typography>
              <Typography variant="h5" color="inherit" paragraph>
                La plataforma que conecta cargas con transportistas de forma rápida y segura
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button variant="contained" size="large" onClick={() => navigate('/register')}>
                  Registrarse
                </Button>
                <Button variant="outlined" size="large" color="inherit" onClick={() => navigate('/loads')}>
                  Ver cargas
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Features Section */}
      <Container sx={{ py: 8 }} maxWidth="lg">
        <Typography variant="h4" component="h2" align="center" gutterBottom>
          ¿Cómo funciona ToPack?
        </Typography>
        <Typography variant="subtitle1" align="center" color="text.secondary" paragraph sx={{ mb: 6 }}>
          Nuestra plataforma facilita el transporte de cargas conectando a personas y empresas con transportistas profesionales
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item key={index} xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 6
                  }
                }}
                elevation={2}
              >
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                  {feature.icon}
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h5" component="h3" align="center">
                    {feature.title}
                  </Typography>
                  <Typography align="center">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How it works section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" align="center" gutterBottom>
            Empieza a usar ToPack en 3 simples pasos
          </Typography>
          
          <Grid container spacing={4} sx={{ mt: 4 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Box 
                  sx={{ 
                    display: 'inline-flex', 
                    borderRadius: '50%', 
                    bgcolor: 'primary.main', 
                    color: 'white',
                    width: 60,
                    height: 60,
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: 2,
                    fontSize: 24,
                    fontWeight: 'bold'
                  }}
                >
                  1
                </Box>
                <Typography variant="h6" gutterBottom>Regístrate</Typography>
                <Typography color="text.secondary">
                  Crea tu cuenta en ToPack y completa tu perfil con tus datos personales
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Box 
                  sx={{ 
                    display: 'inline-flex', 
                    borderRadius: '50%', 
                    bgcolor: 'primary.main', 
                    color: 'white',
                    width: 60,
                    height: 60,
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: 2,
                    fontSize: 24,
                    fontWeight: 'bold'
                  }}
                >
                  2
                </Box>
                <Typography variant="h6" gutterBottom>Publica o busca cargas</Typography>
                <Typography color="text.secondary">
                  Publica tu carga o encuentra transportes disponibles según tus necesidades
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Box 
                  sx={{ 
                    display: 'inline-flex', 
                    borderRadius: '50%', 
                    bgcolor: 'primary.main', 
                    color: 'white',
                    width: 60,
                    height: 60,
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: 2,
                    fontSize: 24,
                    fontWeight: 'bold'
                  }}
                >
                  3
                </Box>
                <Typography variant="h6" gutterBottom>Contacta y coordina</Typography>
                <Typography color="text.secondary">
                  Utiliza nuestro chat para coordinar los detalles y realizar el transporte
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button 
              variant="contained" 
              size="large" 
              onClick={() => navigate('/register')}
              sx={{ px: 4, py: 1.5 }}
            >
              Comenzar ahora
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box 
        sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          py: 6,
          borderRadius: 2,
          mt: 8,
          mb: 4
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" align="center" gutterBottom>
            ¿Listo para empezar?
          </Typography>
          <Typography variant="subtitle1" align="center" paragraph>
            Únete a miles de usuarios que ya confían en ToPack para sus necesidades de transporte
          </Typography>
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button 
              variant="outlined" 
              color="inherit" 
              size="large"
              onClick={() => navigate('/register')}
              sx={{ px: 4, py: 1.5, mr: 2 }}
            >
              Crear cuenta
            </Button>
            <Button 
              variant="outlined" 
              color="inherit" 
              size="large"
              onClick={() => navigate('/delete-account')}
              sx={{ px: 4, py: 1.5 }}
            >
              Eliminar cuenta
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;